/**
 * PostHog utility functions for managing consent and tracking
 * PostHog is configured to work in cookie-less mode (disable_persistence: true)
 * This means it works without requiring cookie consent, but we still respect user preferences
 */

/**
 * Check if analytics cookies are accepted
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const preferences = localStorage.getItem('seoro_cookie_preferences');
    if (!preferences) return false;
    const parsed = JSON.parse(preferences);
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

/**
 * Update PostHog consent based on cookie preferences
 * Call this when cookie preferences change
 * 
 * IMPORTANT: PostHog uses localStorage (not cookies), so it works WITHOUT cookie consent.
 * We only opt out if user EXPLICITLY rejects analytics cookies.
 */
export function updatePostHogConsent(): void {
  if (typeof window === 'undefined') return;
  
  const posthog = (window as any).__posthog;
  if (!posthog) {
    // PostHog not initialized yet, will be handled in loaded callback
    return;
  }
  
  // Check if user has EXPLICITLY rejected analytics cookies
  // If no preference set OR user just hasn't accepted yet, we still ENABLE PostHog
  // (because localStorage doesn't require cookie consent under GDPR)
  const hasExplicitlyRejected = (() => {
    try {
      const preferences = localStorage.getItem('seoro_cookie_preferences');
      if (!preferences) return false; // No preference = ENABLE by default
      const parsed = JSON.parse(preferences);
      // Only opt out if user EXPLICITLY set analytics to false
      return parsed.analytics === false;
    } catch {
      return false; // Error = ENABLE by default
    }
  })();
  
  if (hasExplicitlyRejected) {
    posthog.opt_out_capturing();
  } else {
    // Default: ALWAYS opt in - works without cookie consent
    posthog.opt_in_capturing();
  }
}

/**
 * Check if PostHog is initialized and available
 */
export function isPostHogAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  return typeof (window as any).__posthog !== 'undefined';
}

/**
 * Identify a user in PostHog
 * Call this when a user signs in to link sessions to their user profile
 * @param userId - The user's unique ID (e.g., Supabase user ID)
 * @param userProperties - Optional user properties (email, name, etc.)
 */
export function identifyUser(userId: string, userProperties?: {
  email?: string;
  name?: string;
  [key: string]: any;
}): void {
  if (typeof window === 'undefined') return;
  
  const posthog = (window as any).__posthog;
  if (!posthog) {
    // PostHog not initialized yet, will be handled when it loads
    return;
  }
  
  try {
    posthog.identify(userId, userProperties);
    // Silent identification - no console logging
  } catch (error) {
    // Silent error handling
  }
}

/**
 * Reset PostHog identification (call on sign out)
 */
export function resetUser(): void {
  if (typeof window === 'undefined') return;
  
  const posthog = (window as any).__posthog;
  if (!posthog) {
    return;
  }
  
  try {
    posthog.reset();
    // Silent reset - no console logging
  } catch (error) {
    // Silent error handling
  }
}

